import { useMemo } from 'react'
import {
  addDays,
  addWeeks,
  eachMinuteOfInterval,
  setHours,
  setMinutes,
} from 'date-fns'
import { SchedulerView, SelectedRange } from '../types'
import { ISODateString } from '@pogokid/util/datetime'
import { getVisibleRange } from '../helpers/dateRanges'
import { WeekProps } from '../views/week/types'
import { DayProps } from '../views/day/types'
import { MINUTES_IN_CELL_HEIGHT } from '../helpers/constants.ts'

interface SchedulerRangeDataOptions {
  view: SchedulerView
  selectedDate: ISODateString
  week?: WeekProps | null
  day?: DayProps | null
}

export interface UseSchedulerRangeMemoState {
  startTime: Date
  endTime: Date
  visibleDays: Date[]
  visibleDayTimestamps: Set<number>
  cellTimeSlots: Date[]
  /**
   * Currently visible start and end dates
   */
  visibleRange: SelectedRange
}

export function useCalculateSchedulerRangeData({
  view,
  selectedDate,
  week,
  day,
}: SchedulerRangeDataOptions): UseSchedulerRangeMemoState {
  return useMemo(() => {
    const _selectedDate = new Date(selectedDate)
    const startTime = setMinutes(
      setHours(_selectedDate, week?.startHour ?? 0),
      0
    )
    const endTime = setMinutes(
      setHours(_selectedDate, week?.endHour ?? 23),
      0
    )
    let visibleDays: Date[] = []

    const visibleRange = getVisibleRange(
      view,
      new Date(selectedDate),
      week?.weekStartOn,
      day?.startHour,
      day?.endHour,
      day?.plusDays
    )

    switch (view) {
      case 'week':
        visibleDays = (
          week?.weekDays ?? [0, 1, 2, 3, 4, 5, 6]
        ).map((d) => addDays(visibleRange.start, d))
        break
      case 'weekday':
        visibleDays = [0, 1, 2, 3, 4, 5, 6].map((w) =>
          addWeeks(visibleRange.start, w)
        )
        break
      case 'day':
        visibleDays = [visibleRange.start]
        break
      case 'month':
        visibleDays = []
        break
    }

    // endHour is inclusive, so the last slot starts one
    // step before the end of that hour (23:30 for endHour 23)
    const cellTimeSlots = eachMinuteOfInterval(
      {
        start: startTime,
        end: setMinutes(endTime, 60 - MINUTES_IN_CELL_HEIGHT),
      },
      { step: MINUTES_IN_CELL_HEIGHT }
    )
    return {
      startTime,
      endTime,
      visibleDays,
      visibleDayTimestamps: new Set(
        visibleDays.map((d) => d.valueOf())
      ),
      cellTimeSlots,
      visibleRange,
    }
  }, [
    selectedDate,
    view,
    day?.endHour,
    day?.startHour,
    week?.endHour,
    week?.startHour,
    week?.weekDays,
    week?.weekStartOn,
  ])
}
